import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {LocalStack, Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {Container} from '@app/modules/svg/Container';
import {PointerTool} from '@app/modules/svg/service/tool/pointer/PointerTool';

export interface PrivateWhiteboard extends Whiteboard {}

export namespace PrivateWhiteboardAction {
  export class Reset {
    static readonly type = '[PrivateWhiteboard] reset';

    constructor() {}
  }
  export class SetContainer {
    static readonly type = '[PrivateWhiteboard] set container';

    constructor(public container: Container) {}
  }
  export class SetWrapper {
    static readonly type = '[PrivateWhiteboard] set wrapper element';

    constructor(public wrapper: HTMLDivElement) {}
  }
  export class SetStaffPointerTool {
    static readonly type = '[PrivateWhiteboard] set staff pointer tool';

    constructor(public pointerTool: PointerTool) {}
  }
  export class SetStaffPointersCount {
    static readonly type = '[PrivateWhiteboard] set staff pointer tools count';

    constructor(public count: number) {}
  }
  export class SetScrollPosition {
    static readonly type = '[PrivateWhiteboard] set scroll position';

    constructor(public scrollPosition: number) {}
  }
  export class SetBroadcastScroll {
    static readonly type = '[PrivateWhiteboard] set broadcast scroll';

    constructor(public broadcastScroll: boolean) {}
  }
  export class SetHeight {
    static readonly type = '[PrivateWhiteboard] set height';

    constructor(public height: number) {}
  }
  export class SetWidth {
    static readonly type = '[PrivateWhiteboard] set width';

    constructor(public width: number) {}
  }
  export class SetLastPostedProblemId {
    static readonly type = '[PrivateWhiteboard] set last posted problem id';

    constructor(public id: number) {}
  }
  export class SetStreaming {
    static readonly type = '[PrivateWhiteboard] set streaming';

    constructor(public streaming: boolean) {}
  }
  export class SetSmooth {
    static readonly type = '[PrivateWhiteboard] set smooth drawing';

    constructor(public smooth: boolean) {}
  }

  export class ShareWith {
    static readonly type = '[PrivateWhiteboard] share whiteboard with student';
    constructor(public userId: number) {}
  }
  export class ShareWithAll {
    static readonly type = '[PrivateWhiteboard] share whiteboard with all students';
    constructor() {}
  }
  export class UnshareWith {
    static readonly type = '[PrivateWhiteboard] unshare whiteboard with student';
    constructor(public userId: number) {}
  }
  export class UnshareWithAll {
    static readonly type = '[PrivateWhiteboard] unshare whiteboard with all students';
    constructor() {}
  }
}

@State<PrivateWhiteboard>({
  name: PrivateWhiteboardState.storeName,
  defaults: PrivateWhiteboardState.defaults
})
@Injectable()
export class PrivateWhiteboardState {
  static readonly storeName: string = 'privateWhiteboard';

  static readonly defaults: PrivateWhiteboard = {
    container: undefined,
    height: WhiteboardState.VISIBLE_HEIGHT,
    width: WhiteboardState.VISIBLE_WIDTH,
    lastPostedProblemId: undefined,
    wrapper: undefined,
    scrollPosition: 0,
    broadcastScroll: true,
    localStack: new LocalStack(),
    streaming: false,
    smooth: false,
    sharedUsers: null,
    tools: {
      drawTools: {},
      selectTools: {},
      editNodeTools: {},
      editSegmentTools: {},
      editTableTools: {},
      studentPointerTools: {},
      highlighterTools: {}
    },
    staffPointerTool: undefined,
    staffPointersCount: 0,
    focuses: {}
  };

  @Selector()
  static getWhiteboard(whiteboard: PrivateWhiteboard): PrivateWhiteboard {
    return whiteboard;
  }

  @Action(PrivateWhiteboardAction.Reset)
  reset({setState}: StateContext<PrivateWhiteboard>, {}: PrivateWhiteboardAction.Reset) {
    const defaults: PrivateWhiteboard = JSON.parse(JSON.stringify(PrivateWhiteboardState.defaults));
    defaults.localStack = new LocalStack();

    setState(defaults);
  }

  @Action(PrivateWhiteboardAction.SetContainer)
  setContainer({patchState}: StateContext<PrivateWhiteboard>, {container}: PrivateWhiteboardAction.SetContainer) {
    patchState({container});
  }

  @Action(PrivateWhiteboardAction.SetWrapper)
  setWrapper({patchState}: StateContext<PrivateWhiteboard>, {wrapper}: PrivateWhiteboardAction.SetWrapper) {
    patchState({wrapper});
  }

  @Action(PrivateWhiteboardAction.SetBroadcastScroll)
  setBroadcastScroll({patchState}: StateContext<PrivateWhiteboard>, {broadcastScroll}: PrivateWhiteboardAction.SetBroadcastScroll) {
    patchState({broadcastScroll});
  }

  @Action(PrivateWhiteboardAction.SetHeight)
  setHeight({patchState}: StateContext<PrivateWhiteboard>, {height}: PrivateWhiteboardAction.SetHeight) {
    patchState({height});
  }

  @Action(PrivateWhiteboardAction.SetWidth)
  setWidth({patchState}: StateContext<PrivateWhiteboard>, {width}: PrivateWhiteboardAction.SetWidth) {
    patchState({width});
  }

  @Action(PrivateWhiteboardAction.SetLastPostedProblemId)
  setLastPostedProblemId({patchState}: StateContext<PrivateWhiteboard>, {id}: PrivateWhiteboardAction.SetLastPostedProblemId) {
    patchState({lastPostedProblemId: id});
  }

  @Action(PrivateWhiteboardAction.SetScrollPosition)
  setScrollPosition({patchState}: StateContext<PrivateWhiteboard>, {scrollPosition}: PrivateWhiteboardAction.SetScrollPosition) {
    patchState({scrollPosition});
  }

  @Action(PrivateWhiteboardAction.SetStaffPointerTool)
  setStaffPointerTool({patchState}: StateContext<PrivateWhiteboard>, {pointerTool}: PrivateWhiteboardAction.SetStaffPointerTool) {
    patchState({staffPointerTool: pointerTool});
  }

  @Action(PrivateWhiteboardAction.SetStaffPointersCount)
  setStaffPointerCount({patchState}: StateContext<PrivateWhiteboard>, {count}: PrivateWhiteboardAction.SetStaffPointersCount) {
    patchState({staffPointersCount: count});
  }

  @Action(PrivateWhiteboardAction.SetStreaming)
  setStreaming({patchState}: StateContext<PrivateWhiteboard>, {streaming}: PrivateWhiteboardAction.SetStreaming) {
    patchState({streaming});
  }

  @Action(PrivateWhiteboardAction.SetSmooth)
  setSmooth({patchState}: StateContext<PrivateWhiteboard>, {smooth}: PrivateWhiteboardAction.SetSmooth) {
    patchState({smooth});
  }

  @Action(PrivateWhiteboardAction.ShareWith)
  shareWith({setState, getState}: StateContext<PrivateWhiteboard>, {userId}: PrivateWhiteboardAction.ShareWith) {
    const whiteboard: PrivateWhiteboard = getState();

    if (!whiteboard.sharedUsers) {
      whiteboard.sharedUsers = new Set<number>();
    } else if (whiteboard.sharedUsers.size === 0) { /* shared with all, and can't be shared individually */
      return;
    }

    whiteboard.sharedUsers.add(userId);
    setState(whiteboard);
  }

  @Action(PrivateWhiteboardAction.ShareWithAll)
  shareWithAll({patchState}: StateContext<PrivateWhiteboard>) {
    patchState({sharedUsers: new Set<number>()});
  }

  @Action(PrivateWhiteboardAction.UnshareWith)
  unshareWith({setState, getState}: StateContext<PrivateWhiteboard>, {userId}: PrivateWhiteboardAction.UnshareWith) {
    const whiteboard: PrivateWhiteboard = getState();

    if (whiteboard.sharedUsers) {
      whiteboard.sharedUsers.delete(userId);
      if (whiteboard.sharedUsers.size === 0) { /* if unshared with all, unshare whiteboard */
        whiteboard.sharedUsers = null;
      }
      setState(whiteboard);
    }
  }

  @Action(PrivateWhiteboardAction.UnshareWithAll)
  unshareWithAll({patchState}: StateContext<PrivateWhiteboard>) {
    patchState({sharedUsers: null});
  }
}
